/**
 * One realtime connection, shared by every screen that needs it.
 *
 * The alternative — each screen calling `useRealtime` — would open a socket per screen.
 * The server registers a device by its id and replaces an existing connection when a
 * second arrives, so the screens would take turns evicting each other and the household's
 * presence list would flicker on every navigation.
 *
 * Consumers get `send` and `subscribe`; nothing here exposes the socket itself.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { DeviceType } from '@filmodasi/shared';
import { useRealtime, type Realtime } from './useRealtime';

const RealtimeContext = createContext<Realtime | null>(null);

export function RealtimeProvider({
  children,
  enabled,
  profileId,
  deviceType,
}: {
  children: ReactNode;
  enabled: boolean;
  profileId: string | null;
  deviceType: DeviceType;
}) {
  const realtime = useRealtime({ enabled, profileId, deviceType });
  return <RealtimeContext.Provider value={realtime}>{children}</RealtimeContext.Provider>;
}

/**
 * Returns a no-op channel outside the provider rather than throwing.
 *
 * Screens are rendered before a session exists — the login screen is inside the same tree —
 * and a hook that throws there would make "no connection yet" an error condition every
 * caller had to guard. The remote is a convenience layered on top of an app that works
 * without it; nothing should break when it is absent.
 */
const OFFLINE: Realtime = {
  status: 'idle',
  presence: [],
  send: () => false,
  subscribe: () => () => {},
};

export function useRealtimeChannel(): Realtime {
  return useContext(RealtimeContext) ?? OFFLINE;
}
